/**
 * 促核销组件类型定义
 * 基于 design.md 中的数据模型定义
 */


// ==================== 枚举定义 ====================

/** 优惠券类型枚举 */
export enum CouponType {
  /** 满减券 */
  Discount = 1,
  /** 折扣券 */
  Percentage = 2,
}
// ==================== API 响应数据结构 ====================

/** API响应 - 用户VIP充电卡 */
export interface UserChargeCardDto {
  /** 发放时间（毫秒时间戳） */
  buyTime: number;
  /** 卡面额（单位：元） */
  chargeCardTotalAmt: number;
  /** 卡剩余额度（单位：元） */
  chargeCardValidAmt: number;
  /** 卡模板编号 */
  chargePqNo: string;
  /** 失效时间（毫秒时间戳） */
  invTime: number;
}

/** API响应 - 用户VIP券 */
export interface UserCouponDto {
  /** 优惠券面额 单位：分/折 */
  couponFee: number;
  /** 券ID */
  couponId: string;
  /** 券数量 */
  couponNum: number;
  /** 券计划ID */
  couponPlanId: string;
  /** 优惠券状态名称 */
  couponStatusName: string;
  /** 优惠券类型 1-满减券 2-折扣券 */
  couponType: CouponType;
  /** 过期时间（毫秒时间戳） */
  expireTime: number;
  /** 领券时间（毫秒时间戳） */
  receiveTime: number;
  /** 使用门槛 单位：分 */
  thresholdPrice: number;
}

/** API响应 - 我的会员商品 */
export interface MyMemberGoodsResp {
  /** 用户VIP充电卡列表 */
  userChargeCardList: UserChargeCardDto[];
  /** 用户VIP券列表 */
  userCouponList: UserCouponDto[];
}

// ==================== 统一数据模型 ====================

/** 卡券类型枚举 */
export enum CardItemType {
  /** 优惠券 */
  Coupon = 'coupon',
  /** 储值卡 */
  StoredCard = 'storedCard',
}

/** 统一卡券数据模型 */
export interface UnifiedCardItem {
  /** 唯一标识 */
  id: string;
  /** 卡券类型 */
  itemType: CardItemType;
  /** 到账时间戳（毫秒）—— 券对应 receiveTime，卡对应 buyTime */
  receiveTime: number;
  /** 过期时间戳（毫秒）—— 券对应 expireTime，卡对应 invTime */
  expireTime: number;

  // ===== 优惠券字段（来自 UserCouponDto） =====
  /** 券计划ID，用于聚合（仅优惠券） */
  couponPlanId?: string;
  /** 券ID（仅优惠券） */
  couponId?: string;
  /** 券类型: 1-满减券, 2-折扣券（仅优惠券） */
  couponType?: 1 | 2;
  /** 券面额，单位：分/折（仅优惠券） */
  couponFee?: number;
  /** 使用门槛，单位：分，0表示无门槛（仅优惠券） */
  thresholdPrice?: number;
  /** 券数量（仅优惠券，用于聚合展示） */
  couponNum?: number;
  /** 券状态名称（仅优惠券） */
  couponStatusName?: string;

  // ===== 储值卡字段（来自 UserChargeCardDto） =====
  /** 卡模板编号（仅储值卡） */
  chargePqNo?: string;
  /** 面额（仅储值卡） */
  chargeCardTotalAmt?: number;
  /** 余额（仅储值卡） */
  chargeCardValidAmt?: number;
}

// ==================== 展示数据模型 ====================

/** 卡券分类类型枚举 */
export enum CategoryType {
  /** 新到账（24小时内） */
  NewArrival = 1,
  /** 即将到期（7天内） */
  Expiring = 2,
  /** 其他 */
  Other = 3,
}

/** 聚合数据结构 */
export interface AggregatedData {
  /** 模版ID */
  templateId: string;
  /** 聚合的卡券列表 */
  items: UnifiedCardItem[];
  /** 聚合数量 */
  count: number;
  /** 聚合项中的代表项，用于展示 */
  representative: UnifiedCardItem;
}

/** 展示项数据模型 */
export interface DisplayItem {
  /** 唯一标识 */
  id: string;
  /** 是否为聚合项 */
  isAggregated: boolean;
  /** 分类类型 */
  category: CategoryType;
  /** 原始数据（单张卡券） */
  data?: UnifiedCardItem;
  /** 聚合数据（聚合展示时，仅优惠券支持） */
  aggregatedData?: AggregatedData;
  /** 是否显示新到账角标 */
  showNewBadge: boolean;
  /** 是否显示倒计时 */
  showCountdown: boolean;
}

// ==================== 组件 Props 类型 ====================

/** CouponRedemptionComponent 主组件 Props */
export interface CouponRedemptionComponentProps {
  /** B端配置组件数据 */
  comp?: unknown;
  /** 组件配置 */
  config?: {
    title?: string;
    backgroundColor?: string;
    padding?: string;
  };
  /** 曝光人群ID */
  audienceId?: string;
}

/** RedemptionCard 统一卡片组件 Props */
export interface RedemptionCardProps {
  /** 卡券数据（统一数据结构） */
  item: UnifiedCardItem;
  /** 是否显示新到账角标 */
  showNewBadge?: boolean;
  /** 是否显示倒计时 */
  showCountdown?: boolean;
  /** 聚合数量（聚合展示时使用，仅优惠券支持） */
  aggregateCount?: number;
  /** 点击回调 */
  onClick?: () => void;
}

/** CountdownTimer 倒计时组件 Props */
export interface CountdownTimerProps {
  /** 过期时间戳 */
  expireTime: number;
  /** 倒计时结束回调 */
  onExpire?: () => void;
}

/** useCouponRedemption Hook 返回值 */
export interface UseCouponRedemptionReturn {
  /** 处理后的卡券列表 */
  displayList: DisplayItem[];
  /** 是否加载中 */
  loading: boolean;
  /** 是否应该展示组件 */
  shouldShow: boolean;
  /** 刷新数据 */
  refresh: () => void;
}

// ==================== 常量定义 ====================

/** 24小时毫秒数 */
export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** 7天毫秒数 */
export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/** 1天毫秒数 */
export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** 聚合阈值 */
export const AGGREGATION_THRESHOLD = 5;

/** 默认组件标题 */
export const DEFAULT_COMPONENT_TITLE = '权益·我的会员产品';
